// For parts whose section lists things per space (topics, documents, issues):
// the spaces one can go to, each as a group under its name.
//
// Promise: archived spaces and spaces with the part's feature off are left out;
// a workspace with one space shows no space headings (its things are simply
// the workspace's things). A heading may open the space (`headingTo`).
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { SpaceView } from "@trameria/cloud-api/workspace-shared";
import { SectionHeading, useApi } from "@trameria/ui-core";
import type { ReactNode } from "react";
import { useWorkspace } from "./context";
import { spacesQuery } from "./queries";

/** The spaces of the workspace one can go to (not archived), in one's order. */
export function useWorkspaceSpaces(): SpaceView[] {
  const { slug } = useWorkspace();
  const spaces = useQuery(spacesQuery(useApi(), slug));
  return (spaces.data ?? []).filter((s) => s.archivedAt === null);
}

export function SpaceGroups({
  feature,
  only,
  headingTo,
  children,
}: {
  /** Only spaces with this feature on. */
  feature?: string;
  /** Only this space (a list narrowed to one space). */
  only?: string | null;
  /** Where a space's heading leads (its overview); a plain heading without it. */
  headingTo?(space: SpaceView): string;
  children(space: SpaceView): ReactNode;
}) {
  const all = useWorkspaceSpaces();
  const shown = (
    feature ? all.filter((s) => s.features.includes(feature)) : all
  ).filter((s) => !only || s.id === only);
  const headings = all.length > 1;
  return (
    <>
      {shown.map((space) => (
        <section key={space.id} aria-label={space.name}>
          {headings && (
            <SectionHeading>
              {headingTo ? (
                <Link
                  to={headingTo(space)}
                  className="rounded-[4px] hover:text-body hover:underline"
                >
                  {space.name}
                </Link>
              ) : (
                space.name
              )}
            </SectionHeading>
          )}
          {children(space)}
        </section>
      ))}
    </>
  );
}
